import axios, { AxiosResponse } from "axios";
import { useContext } from "react";
import { MutationFunction, useMutation, UseMutationOptions, useQuery, UseQueryOptions } from "react-query";
import {
  apiGetAllEquipmentsByPlantID,
  apiDocuments,
  apiPlantEquipment,
  apiReportDownloadFile,
  apiSiteUnits,
  technicalBulletinApiUrl,
  omManualApiUrl,
  apiPartsCatalog
} from "../../endpoints/endpoints";
import { queryKeys } from "../../react-query/queryKeys";
import { Store } from "../../Store";
import { CPApiResponse, PlantEquipmentsDATA, Document, SiteUnit } from "../../utils/interface";
import { IPItemTypes } from "../../utils/IPItemTypes";

function handleAxiosResponse<T = any>(response: AxiosResponse<CPApiResponse<T>>): T {
  if (response.status >= 400) throw response.statusText;
  if (response.data.success !== true) throw response.data.message;
  return response.data.data;
}

const fetchPlantSiteUnits = async (plantId: number): Promise<SiteUnit[]> => {
  const response: AxiosResponse<CPApiResponse<SiteUnit[]>> = await axios(`${apiSiteUnits}/GetAllByPlantId?PlantId=${plantId}`);
  return handleAxiosResponse(response);
};

export const usePlantSiteUnits = (options: Omit<UseQueryOptions<SiteUnit[]>, "queryKey" | "queryFn"> = {}) => {
  const { selectedPlant } = useContext(Store);
  const plantId = selectedPlant.plantId;
  return useQuery<SiteUnit[]>(["plantSiteUnits", plantId], () => fetchPlantSiteUnits(plantId), {
    ...options,
  });
};

const fetchPlantSiteUnitEquipment = async (unitId: number): Promise<PlantEquipmentsDATA[]> => {
  const response: AxiosResponse<CPApiResponse<PlantEquipmentsDATA[]>> = await axios(`${apiPlantEquipment}/GetAllByUnitId?UnitId=${unitId}`);
  return handleAxiosResponse(response);
};

export const usePlantSiteUnitEquipment = (unitId: number, options: Omit<UseQueryOptions<PlantEquipmentsDATA[]>, "queryKey" | "queryFn"> = {}) => {
  return useQuery<PlantEquipmentsDATA[]>(["plantSiteUnitEquipments", unitId], () => fetchPlantSiteUnitEquipment(unitId), options);
};

const createNewDocument: MutationFunction<Document, FormData> = async (formData: FormData): Promise<Document> => {
  const response: AxiosResponse<CPApiResponse<Document>> = await axios.post(`${apiDocuments}${formData.get("accessType") === IPItemTypes.IPDocument.toString() ? "" : "-la"}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return handleAxiosResponse(response);
};
export const useCreateNewDocument = (
  options: Omit<UseMutationOptions<Document, unknown, FormData, unknown>, "mutationKey" | "mutationFn"> = {}
) => {
  return useMutation("", createNewDocument, options);
};

const updateDocument: MutationFunction<Document, [number, FormData]> = async ([globalId, formData]): Promise<Document> => {
  const response: AxiosResponse<CPApiResponse<Document & {displayName: string}>> = await axios.put(`${apiDocuments}${formData.get("accessType") === IPItemTypes.IPDocument.toString() ? "" : "-la"}/${globalId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  const data = handleAxiosResponse(response);
  data.displayName = data.displayName;
  return data;
};
export const useUpdateDocument = (
  options: Omit<UseMutationOptions<Document, unknown, [number, FormData], unknown>, 'mutationKey' | 'mutationFn'> = {}
) => {
  return useMutation('', updateDocument, options);
};

// Create New OmmManual     Create New | Use Create New -  Update |  Use update
const createNewOMManual: MutationFunction<Document, FormData> = async (formData: FormData): Promise<Document> => {
	const response: AxiosResponse<CPApiResponse<Document>> = await axios.post(omManualApiUrl, formData, {
		headers: {
			"Content-Type": "multipart/form-data",
		},
	});
	return handleAxiosResponse(response);
};
export const useCreateNewOMManual = (options: Omit<UseMutationOptions<Document, unknown, FormData, unknown>, "mutationKey" | "mutationFn"> = {}) => {
	return useMutation("", createNewOMManual, options);
};

const updateOMManual: MutationFunction<Document, [number, FormData]> = async ([globalId, formData]): Promise<Document> => {
  const response: AxiosResponse<CPApiResponse<Document & {displayName: string}>> = await axios.put(`${omManualApiUrl}/${globalId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  const data = handleAxiosResponse(response);
  data.displayName = data.displayName;
  return data;
};
export const useUpdateOMManual = (
  options: Omit<UseMutationOptions<Document, unknown, [number, FormData], unknown>, 'mutationKey' | 'mutationFn'> = {}
) => {
  return useMutation('', updateOMManual, options);
};

// Parts Catalog (moduleID = 4) section   Create new | Use Create New  -  update  |  Use update

const createNewPartsCatalog: MutationFunction<Document, FormData> = async (formData: FormData): Promise<Document> => {
	const response: AxiosResponse<CPApiResponse<Document>> = await axios.post(apiPartsCatalog, formData, {
		headers: {
			"Content-Type": "multipart/form-data",
		},
	});
	return handleAxiosResponse(response);
};
export const useNewPartsCatalog = (options: Omit<UseMutationOptions<Document, unknown, FormData, unknown>, "mutationKey" | "mutationFn"> = {}) => {
	return useMutation("", createNewPartsCatalog, options);
};

const updateNewPartsCatalog: MutationFunction<Document, [number, FormData]> = async ([globalId, formData]): Promise<Document> => {
  const response: AxiosResponse<CPApiResponse<Document & {displayName: string}>> = await axios.put(`${apiPartsCatalog}/${globalId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  const data = handleAxiosResponse(response);
  data.displayName = data.displayName;
  return data;
};
export const useUpdateNewPartsCatalog = (
  options: Omit<UseMutationOptions<Document, unknown, [number, FormData], unknown>, 'mutationKey' | 'mutationFn'> = {}
) => {
  return useMutation('', updateNewPartsCatalog, options);
};



// Get units by Equipment section
const getUnitIdsByEquipmentIds = async (equipmentIds: number[], plantId: number): Promise<number[]> => {
  const response: AxiosResponse<CPApiResponse<PlantEquipmentsDATA[]>> = await axios(`${apiGetAllEquipmentsByPlantID}?PlantId=${plantId}`);
  const plantEquipment = handleAxiosResponse(response);
  return plantEquipment
    .filter((d) => equipmentIds.includes(d.plantEquipmentId))
    .map((d) => d.unitID)
    .filter((v: number, i: number, a: number[]) => a.indexOf(v) === i);
};

export const useUnitsOfEquipmentIds = (
  equipmentIds: number[],
  options: Omit<UseQueryOptions<number[]>, "queryKey" | "queryFn"> = { initialData: [] }
) => {
  const { selectedPlant } = useContext(Store);
  const { plantId } = selectedPlant;
  return useQuery<number[]>(["plantEquipments", plantId, ...equipmentIds.sort()], () => getUnitIdsByEquipmentIds(equipmentIds, plantId), options);
};

// TBReports Crud Method Section
export interface TBReportCrudMethods<T, TD, R> {
  update: (data: TD) => Promise<R>;
  create: (data: TD) => Promise<R>;
  getFile: (globalItemId: number) => Promise<Blob>;
}

export const TBReportAPI: TBReportCrudMethods<AxiosResponse<Document>, FormData, Document> = {
  // --------------------------------------------------------------------------//

  update: async (data: FormData): Promise<Document> => {
    const globalItemId = data.get("globalItemId");
    const response = await axios({
      url: `${technicalBulletinApiUrl}/${globalItemId}`,
      method: "PUT",
      headers: {
        "Content-Type": "multipart/form-data",
      },
      data: data,
    });
    return handleAxiosResponse(response);
  },

  // --------------------------------------------------------------------------//

  create: async (data: FormData): Promise<Document> => {
    const response = await axios({
      url: `${technicalBulletinApiUrl}`,
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
      data: data,
    });
    return handleAxiosResponse(response);
  },

  // --------------------------------------------------------------------------//

  getFile: async (globalItemId: number): Promise<Blob> => {
    const response = await axios({
      url: `${apiReportDownloadFile}/${globalItemId}`,
      method: "GET",
      responseType: "blob",
    });
    return response.data;
  },
};

// --------------------------------------------------------------------------//

export const useUpdate_TBReport = () => {
  return useMutation(TBReportAPI.update);
};

export const useGetFile_TBReport = (globalItemId: number, options: Omit<UseQueryOptions<Blob>, "queryKey" | "queryFn">) => {
  return useQuery([queryKeys.File, globalItemId], () => TBReportAPI.getFile(globalItemId), options);
};

export const useCreate_TBReport = () => {
  return useMutation(TBReportAPI.create);
};

// --------------------------------------------------------------------------//
